import React from 'react';
import PropTypes from 'prop-types';

// @mui material components
import { Table as MuiTable } from '@mui/material';
import TableBody from '@mui/material/TableBody';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';

// Argon Dashboard 2 MUI components
import ArgonBox from 'components/ArgonBox';
import ArgonAvatar from 'components/ArgonAvatar';
import ArgonTypography from 'components/ArgonTypography';

// Argon Dashboard 2 MUI base styles
import typography from 'assets/theme/base/typography';
import borders from 'assets/theme/base/borders';

function StageCommitTable({ columns, rows }) {
  const { size, fontWeightBold } = typography;
  const { borderWidth } = borders;

  const renderColumns = columns.map(({ name, align, width }, key) => {
    let pl;
    let pr;

    if (key === 0) {
      pl = 3;
      pr = 3;
    } else if (key === columns.length - 1) {
      pl = 3;
      pr = 3;
    } else {
      pl = 1;
      pr = 1;
    }

    return (
      <ArgonBox
        key={name}
        component="th"
        width={width || 'auto'}
        pt={1.5}
        pb={1.25}
        pl={align === 'left' ? pl : 3}
        pr={align === 'right' ? pr : 3}
        textAlign={align}
        fontSize={size.xxs}
        fontWeight={fontWeightBold}
        color="secondary"
        opacity={0.7}
        sx={({ palette: { light } }) => ({ borderBottom: `${borderWidth[1]} solid ${light.main}` })}
      >
        {name.toUpperCase()}
      </ArgonBox>
    );
  });

  const renderRows = rows.map((row, rowIndex) => {
    const rowKey = `row-${rowIndex}`;

    const tableRow = columns.map(({ name, align }, colIndex) => {
      let template;

      if (Array.isArray(row[name])) {
        template = (
          <ArgonBox
            key={`cell-${rowIndex}-${colIndex}`}
            component="td"
            p={1}
            sx={({ palette: { light } }) => ({
              borderBottom: row.hasBorder ? `${borderWidth[1]} solid ${light.main}` : null,
            })}
          >
            <ArgonBox display="flex" alignItems="center" py={0.5} px={1}>
              <ArgonBox mr={2}>
                <ArgonAvatar src={row[name][0]} name={row[name][1]} variant="rounded" size="sm" />
              </ArgonBox>
              <ArgonTypography variant="button" fontWeight="medium" sx={{ width: 'max-content' }}>
                {row[name][1]}
              </ArgonTypography>
            </ArgonBox>
          </ArgonBox>
        );
      } else if (typeof row[name] === 'object' && row[name] !== null && React.isValidElement(row[name])) {
        // Check if it's a React component
        template = (
          <ArgonBox
            key={`cell-${rowIndex}-${colIndex}`}
            component="td"
            p={1.2}
            textAlign={align}
            lineHeight={0.65}
            sx={({ palette: { light } }) => ({
              borderBottom: row.hasBorder ? `${borderWidth[1]} solid ${light.main}` : null,
            })}
          >
            {row[name]}
          </ArgonBox>
        );
      } else {
        template = (
          <ArgonBox
            key={`cell-${rowIndex}-${colIndex}`}
            component="td"
            p={1.2}
            textAlign={align}
            lineHeight={0.65}
            sx={({ palette: { light } }) => ({
              borderBottom: row.hasBorder ? `${borderWidth[1]} solid ${light.main}` : null,
            })}
          >
            <ArgonTypography
              variant="button"
              fontWeight="regular"
              color="secondary"
              sx={{
                display: 'inline-block',
                width: 'max-content',
                paddingLeft: '1rem',
                paddingRight: '1rem',
              }}
            >
              {row[name]}
            </ArgonTypography>
          </ArgonBox>
        );
      }

      return template;
    });

    return <TableRow key={rowKey}>{tableRow}</TableRow>;
  });

  return (
    <TableContainer>
      <MuiTable>
        <ArgonBox component="thead">
          <TableRow>{renderColumns}</TableRow>
        </ArgonBox>
        <TableBody>{renderRows}</TableBody>
      </MuiTable>
    </TableContainer>
  );
}

// Setting default values for the props of Table
StageCommitTable.defaultProps = {
  columns: [],
  rows: [{}],
};

// Typechecking props for the Table
StageCommitTable.propTypes = {
  columns: PropTypes.arrayOf(PropTypes.object),
  rows: PropTypes.arrayOf(PropTypes.object),
};

export default StageCommitTable;
